import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import Pagination from "@/components/Pagination";
import { Star, LoaderCircle, MessageSquare, Reply, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;
const HOTEL_ROLES = ["otel_owner", "otel_manager", "otel_reception", "otel_accounting"];
const REPLY_ROLES = ["otel_owner", "otel_manager"]; // reception/accounting görür ama cevap yazamaz

const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

const StatusPill = ({ status }) => {
    const map = {
        pending: "bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
        approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
        rejected: "bg-rose-100 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300",
    };
    const labels = { pending: "Yönetici Onayı Bekliyor", approved: "Yayında", rejected: "Reddedildi" };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${map[status] || "bg-slate-100"}`}>{labels[status] || status}</span>;
};

function ReplyBox({ review, canReply, onSubmit, onDelete }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(review.hotel_reply || "");
    const [saving, setSaving] = useState(false);

    const hasReply = !!review.hotel_reply;

    if (hasReply && !open) {
        return (
            <div data-testid={`reply-shown-${review.id}`} className="mt-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900">
                <div className="flex items-start gap-2">
                    <Reply className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400 mb-1">Otel Cevabı · {fmtDate(review.hotel_reply_at)}</div>
                        <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{review.hotel_reply}</p>
                    </div>
                    {canReply && (
                        <div className="flex gap-1">
                            <button data-testid={`reply-edit-${review.id}`} onClick={() => setOpen(true)} className="text-[11px] font-semibold text-sky-600 hover:underline">Düzenle</button>
                            <button data-testid={`reply-delete-${review.id}`} onClick={() => onDelete(review.id)} className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-3 w-3" /></button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (!canReply) return null;

    if (!open) {
        return (
            <button data-testid={`reply-open-${review.id}`} onClick={() => setOpen(true)} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700">
                <Reply className="h-3.5 w-3.5" /> Cevap Yaz
            </button>
        );
    }

    return (
        <div className="mt-3 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-900">
            <textarea data-testid={`reply-textarea-${review.id}`} rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Misafire profesyonel ve nazik bir cevap yazın..." className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-sky-300 dark:border-sky-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40" />
            <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-slate-500">{text.length}/2000 karakter</span>
                <div className="flex gap-2">
                    <button onClick={() => { setOpen(false); setText(review.hotel_reply || ""); }} className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-semibold">İptal</button>
                    <button data-testid={`reply-submit-${review.id}`} disabled={saving || !text.trim()} onClick={async () => { setSaving(true); await onSubmit(review.id, text); setSaving(false); setOpen(false); }} className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white font-bold inline-flex items-center gap-1"><Send className="h-3 w-3" /> {saving ? "Gönderiliyor..." : (hasReply ? "Güncelle" : "Gönder")}</button>
                </div>
            </div>
        </div>
    );
}

export default function HotelReviewsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [reviews, setReviews] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [counts, setCounts] = useState({});
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);

    const canReply = user && REPLY_ROLES.includes(user.role);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
            if (filter) params.status = filter;
            const r = await api.get("/hotel/reviews", { params });
            setReviews(r.data.items || []);
            setTotal(r.data.total || 0);
            setCounts(r.data.counts || {});
        } finally { setLoading(false); }
    }, [filter, page]);

    /* eslint-disable */
    useEffect(() => {
        if (authLoading) return;
        if (!user) return navigate("/h/login");
        if (!HOTEL_ROLES.includes(user.role)) return navigate("/admin");
        load();
    }, [user, authLoading, navigate, load]);

    // Filtre değişince sayfayı 1'e dön
    useEffect(() => { setPage(1); }, [filter]);
    /* eslint-enable */

    const submitReply = async (id, text) => {
        try {
            await api.post(`/hotel/reviews/${id}/reply`, { text });
            toast.success("Cevap gönderildi");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    };

    const deleteReply = async (id) => {
        if (!window.confirm("Cevabınız silinsin mi?")) return;
        try {
            await api.delete(`/hotel/reviews/${id}/reply`);
            toast.success("Cevap silindi");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    };

    return (
        <HotelLayout title="Misafir Yorumları">
            <div className="mb-4 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-900 text-xs text-sky-800 dark:text-sky-300">
                ℹ️ Yorumların onaylanması ve yayınlanması platform yöneticisi tarafından yapılmaktadır. Bu sayfadan <b>yalnızca misafir yorumlarına cevap yazabilirsiniz</b>.
            </div>
            <div className="flex items-center gap-2 mb-4">
                {[
                    { v: "", label: "Tümü", c: total },
                    { v: "pending", label: "Onay Bekleyen", c: counts.pending || 0 },
                    { v: "approved", label: "Yayında", c: counts.approved || 0 },
                    { v: "rejected", label: "Reddedilen", c: counts.rejected || 0 },
                ].map((t) => (
                    <button key={t.v} data-testid={`h-reviews-filter-${t.v || "all"}`} onClick={() => setFilter(t.v)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${filter === t.v ? "bg-emerald-500 text-white" : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300"}`}>
                        {t.label} <span className="ml-1 opacity-70">({t.c})</span>
                    </button>
                ))}
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                {loading ? (
                    <div className="p-12 flex items-center justify-center"><LoaderCircle className="h-6 w-6 text-emerald-500 animate-spin" /></div>
                ) : reviews.length === 0 ? (
                    <div className="p-12 text-center text-sm text-slate-500" data-testid="h-reviews-empty">
                        <MessageSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        Henüz yorum yok.
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {reviews.map((r) => (
                            <div key={r.id} data-testid={`h-review-${r.id}`} className="p-5 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                            <span className="font-bold text-slate-900 dark:text-white">{r.guest_name || "Anonim"}</span>
                                            <div className="flex items-center gap-0.5">
                                                {[1, 2, 3, 4, 5].map((s) => (
                                                    <Star key={s} className={`h-3.5 w-3.5 ${s <= (r.rating || 0) ? "text-amber-400 fill-amber-400" : "text-slate-300 dark:text-slate-700"}`} />
                                                ))}
                                            </div>
                                            <StatusPill status={r.status || "pending"} />
                                        </div>
                                        <div className="text-[11px] text-slate-500">{fmtDate(r.created_at)} · {r.guest_email || ""}</div>
                                    </div>
                                </div>
                                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{r.comment || r.text || "—"}</p>
                                <ReplyBox review={r} canReply={canReply} onSubmit={submitReply} onDelete={deleteReply} />
                            </div>
                        ))}
                    </div>
                )}
                <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="yorum" />
            </div>
        </HotelLayout>
    );
}
